import React, { useState, useEffect } from 'react';
import { FaTools, FaArrowRight, FaFire, FaStar } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import './inventory.css';
import { useApp } from '../../context/AppContext';
import { useScrollAnimation, useStaggeredAnimation } from '../../hooks/useScrollAnimation';
import { getAnnouncementUrl } from '../../utils/slugify';
import { getApiEndpoint, getUploadUrl } from '../../config/api';

const Inventory = () => {
    const { t } = useApp();
    const navigate = useNavigate();
    const [machineryData, setMachineryData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalCount, setTotalCount] = useState(0);

    // Scroll animations
    const headerRef = useScrollAnimation('fade-up');
    const cardsRefs = useStaggeredAnimation(6, 'scale-in', 100);

    useEffect(() => {
        fetchLatestAnnouncements();
    }, []);

    const fetchLatestAnnouncements = async () => {
        try {
            setLoading(true);
            const response = await fetch(getApiEndpoint('/api/announcements/all'));
            const data = await response.json();

            if (data.success) {
                // Get last 6 announcements
                const last6 = data.announcements.slice(0, 6);
                setTotalCount(data.count);
                
                // Map database announcements to component format
                const mappedData = last6.map(announcement => {
                    // Map condition from database to translation key
                    let conditionKey = 'good';
                    if (announcement.condition) {
                        const cond = announcement.condition.toLowerCase();
                        if (cond.includes('excellent') || cond.includes('excelent')) conditionKey = 'excellent';
                        else if (cond.includes('very good') || cond.includes('foarte bun')) conditionKey = 'veryGood';
                        else if (cond.includes('like new') || cond.includes('ca nou')) conditionKey = 'likeNew';
                        else if (cond.includes('good') || cond.includes('bun')) conditionKey = 'good';
                    }

                    // Get image path - fix the URL
                    let imageUrl = 'https://via.placeholder.com/400x300?text=No+Image';
                    if (announcement.images) {
                        const firstImage = announcement.images.split(',')[0].trim();
                        // Images are served from /uploads via the static middleware
                        imageUrl = getUploadUrl(firstImage);
                    }

                    return {
                        id: announcement.id,
                        title: announcement.title, // Keep original title for URL slugs
                        name: announcement.title,
                        category: announcement.category,
                        subcategory: announcement.subcategory,
                        created_at: announcement.created_at,
                        manufacturer: announcement.manufacturer,
                        model: announcement.model,
                        location: announcement.location,
                        price: new Intl.NumberFormat('de-DE').format(announcement.price),
                        hours: new Intl.NumberFormat('de-DE').format(announcement.hours),
                        year: announcement.year,
                        image: imageUrl,
                        condition: conditionKey,
                        negotiable: announcement.negotiable
                    };
                });

                setMachineryData(mappedData);
            }
        } catch (error) {
            console.error('Error fetching announcements:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <section className="inventory-section">
                <div className="container">
                    <div className="section-header">
                        <div className="section-badge">
                            <FaTools /> {t.inventory.badge}
                        </div>
                        <h2 className="section-title">
                            {t.inventory.title.part1} <span className="highlight">{t.inventory.title.part2}</span>
                        </h2>
                    </div>
                    <div style={{ textAlign: 'center', padding: '50px', color: '#666' }}>
                        Loading announcements...
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="inventory-section">
            <div className="inventory-container">
                {/* Modern Header with Icon */}
                <div className="inventory-header" ref={headerRef}>
                    <div className="header-icon-wrapper">
                        <FaFire className="fire-icon" />
                    </div>
                    <div className="header-badge">
                        <FaStar /> {t.inventory.badge}
                    </div>
                    <h2 className="inventory-title">
                        {t.inventory.title.part1} <span className="title-highlight">{t.inventory.title.part2}</span>
                    </h2>
                    <p className="inventory-description">
                        {t.inventory.subtitle}
                    </p>
                </div>

                {/* Cards Grid with Modern Design */}
                <div className="inventory-grid">
                    {machineryData.map((machine, index) => (
                        <div 
                            key={machine.id} 
                            className="inventory-card"
                            style={{ animationDelay: `${index * 0.1}s` }}
                            onClick={() => navigate(getAnnouncementUrl(machine.id, machine.title, machine.category, machine.subcategory, machine.created_at))}
                            ref={el => cardsRefs.current[index] = el}
                        >
                            {/* Image Container */}
                            <div className="card-image-container">
                                <img 
                                    src={machine.image} 
                                    alt={machine.name}
                                    className="card-image"
                                    onError={(e) => {
                                        e.target.src = 'https://via.placeholder.com/400x300?text=No+Image';
                                    }}
                                />
                                
                                {/* Category Badge - Top Left */}
                                <div className="category-badge-top">
                                    {t.inventory.categories[machine.category.toLowerCase()] || machine.category}
                                </div>
                                
                                {/* Condition Badge - Top Left Below Category */}
                                <div className="condition-badge-top">
                                    {t.inventory.condition[machine.condition]}
                                </div>
                                
                                {/* Negotiable Badge - Top Left */}
                                {machine.negotiable === 1 && (
                                    <div className="negotiable-badge-top">
                                        💬 {t.inventory.negotiable}
                                    </div>
                                )}
                            </div>
                            
                            {/* Card Content */}
                            <div className="card-info-section">
                                <h3 className="card-title-grid">{machine.name}</h3>
                                
                                {/* Quick Stats - 3 Columns */}
                                <div className="card-stats-grid">
                                    <div className="stat-item-card">
                                        <span className="stat-label">YEAR</span>
                                        <span className="stat-value">{machine.year}</span>
                                    </div>
                                    <div className="stat-item-card">
                                        <span className="stat-label">HOURS</span>
                                        <span className="stat-value">{machine.hours}H</span>
                                    </div>
                                    <div className="stat-item-card">
                                        <span className="stat-label">LOCATION</span>
                                        <span className="stat-value">{machine.location}</span>
                                    </div>
                                </div>

                                {/* Manufacturer Info */}
                                <div className="manufacturer-info">
                                    <span className="manufacturer-label">{machine.manufacturer}</span>
                                    <span className="model-label">{machine.model}</span>
                                </div>
                            </div>

                            {/* Price and Action Section */}
                            <div className="card-actions-section">
                                <div className="price-container-grid">
                                    <span className="price-label">PRICE</span>
                                    <div className="price-value">
                                        <span className="euro-icon">€</span>
                                        <span className="price-amount">{machine.price}</span>
                                    </div>
                                </div>
                                
                                <button 
                                    className="btn-view-details"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(getAnnouncementUrl(machine.id, machine.title, machine.category, machine.subcategory, machine.created_at));
                                    }}
                                >
                                    {t.inventory.viewDetails}
                                    <FaArrowRight />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Modern View All Button */}
                <div className="view-all-section">
                    <button 
                        onClick={() => navigate('/machines')}
                        className="view-all-btn-modern"
                    >
                        <span>{t.inventory.viewAll}</span>
                        <div className="btn-count">{totalCount}+</div>
                        <FaArrowRight className="btn-arrow" />
                    </button>
                </div>
            </div>
        </section>
    );
};

export default Inventory;
